import { useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { usePreferencesStore } from '@/stores/preferences-store';
import { useToastStore } from '@/stores/toast-store';

interface AlertableNotification {
  id: string;
  title: string;
  body: string;
  actionUrl?: string | null;
}

/** Vrai si le navigateur sait afficher des notifications natives et que l'utilisateur l'a permis. */
export function canNotifyNatively(): boolean {
  return typeof window.Notification === 'function' && window.Notification.permission === 'granted';
}

/**
 * Signale les notifications ARRIVÉES depuis l'ouverture de l'application, selon les
 * préférences de l'appareil :
 *  - onglet en arrière-plan → notification native du système, si elle est activée et
 *    autorisée (c'est le seul moyen d'atteindre quelqu'un qui regarde ailleurs) ;
 *  - onglet au premier plan → bandeau dans l'application, s'il est activé.
 *
 * Le premier lot reçu sert de référence et n'alerte pas : rouvrir l'application ne doit pas
 * rejouer toutes les notifications encore non lues.
 */
export function useNotificationAlerts(notifications: readonly AlertableNotification[] | undefined) {
  const seen = useRef<Set<string> | null>(null);
  const navigate = useNavigate();
  const showToast = useToastStore((state) => state.show);

  useEffect(() => {
    if (notifications === undefined) {
      return;
    }

    if (seen.current === null) {
      seen.current = new Set(notifications.map((notification) => notification.id));

      return;
    }

    const known = seen.current;
    const fresh = notifications.filter((notification) => !known.has(notification.id));
    fresh.forEach((notification) => known.add(notification.id));

    const { osNotifications, browserNotifications } = usePreferencesStore.getState();

    for (const notification of fresh) {
      if (document.visibilityState !== 'visible') {
        if (osNotifications && canNotifyNatively()) {
          const native = new window.Notification(notification.title, { body: notification.body, tag: notification.id });

          native.onclick = () => {
            window.focus();

            if (notification.actionUrl) {
              void navigate({ href: notification.actionUrl });
            }
          };
        }
      } else if (browserNotifications) {
        showToast({
          id: notification.id,
          title: notification.title,
          body: notification.body,
          actionUrl: notification.actionUrl ?? null,
        });
      }
    }
  }, [notifications, navigate, showToast]);
}
